import React from "react";
import {
  ReactEditor,
  useSlateStatic
} from 'slate-react';
import {
  Transforms } from 'slate';
import { css } from '@emotion/css';
import ImageElement from "./imageElement";
import TableElement from "./tableElement";
import styles from "../editor.module.scss";
import VideoElement from "./videoElement";
import EmbedElement from "./embedElement";
import LinkPreviewElement from "./linkPreviewElement";
import {
  LayoutElement,
  LayoutRowElement,
  LayoutColElement
} from "./layoutElements";
import { KoCheckBox } from "packages/checkbox";

export const Element = ({ attributes, children, element, attachments, readOnly }) => {
  const style = { textAlign: element.align || "left" };
  const { checked } = element;
  const editor = useSlateStatic();
  if (element.type === 'numbered-list') {
    style.listStyleType = 'decimal';
  }

  if (element.type === 'block-quote') {
    style.borderLeft = "4px solid var(--border)";
    style.margin = ".3rem 0";
    style.padding = "0.1rem .5rem";
    style.color = "var(--palette-gray-8)";
  }

  switch (element.type) {
  case "block-quote":
    return (
      <blockquote {...attributes} style={style}>
        {children}
      </blockquote>
    );

  case "bulleted-list":
    return <ul {...attributes} style={style}>{children}</ul>;

  case "numbered-list":
    return <ol {...attributes} style={style}>{children}</ol>;

  case "list-item":
    return <li {...attributes} style={style}>{children}</li>;

  case "h1":
    return <h1 style={style} {...attributes}>{children}</h1>;

  case "h2":
    return <h2 style={style} {...attributes}>{children}</h2>;

  case "h3":
    return <h3 style={style} {...attributes}>{children}</h3>;

  case "h4":
    return <h4 style={style} {...attributes}>{children}</h4>;

  case "h5":
    return <h5 style={style} {...attributes}>{children}</h5>;

  case "h6":
    return <h6 style={style} {...attributes}>{children}</h6>;

  case "image": {
    const file = attachments?.find(f => (f.fileId && f.fileId === element.fileId) || (f.uid && f.uid === element.fileId));
    const url = file?.url || element.url;
    return <ImageElement readOnly={readOnly} element={element} url={url} attributes={attributes}>{children}</ImageElement>;
  }

  case "table":
    return <TableElement element={element} attributes={attributes}>{children}</TableElement>;

  case "table-row":
    return <tr {...attributes}>{children}</tr>;

  case "table-cell":
    return <td {...attributes} className={styles.td}>{children}</td>;

  case "table-header-cell":
    return <th {...attributes} className={styles.td}>{children}</th>;

  case "link-preview":
    return <LinkPreviewElement element={element} attributes={attributes} />;

  case "code-block":
    return (
      <pre {...attributes}>
        <code>{children}</code>
      </pre>
    );
  case 'check-list-item':
    return (<div
      {...attributes}
      className={css`
          display: flex;
          flex-direction: row;
          align-items: center;
  
          & + & {
            margin-top: 0;
          }
        `}
    >
      <span
        contentEditable={false}
        className={css`
            margin-right: 0.75em;
          `}
      >
        <KoCheckBox disabled={true} checked={checked} onChange={event => {
          const path = ReactEditor.findPath(editor, element);
          const newProperties = {
            checked: event.target.checked
          };
          Transforms.setNodes(editor, newProperties, { at: path });
        }} />
      </span>
      <span
        contentEditable={!readOnly}
        suppressContentEditableWarning
        className={css`
            flex: 1;
            opacity: ${checked ? 0.666 : 1};
            color: ${!checked ? 'inherit' : 'var(--palette-primary-dark)'};
            &:focus {
              outline: none;
            }
          `}
      >
        {children}
      </span>
    </div>
    );
  case "video": {
    const fileObj = attachments?.find(f => (f.fileId || f.uid) === element.fileId);
    const videourl = fileObj?.url || element.url;
    return <VideoElement readOnly={readOnly} url={videourl} element={element} attributes={attributes}>{children}</VideoElement>;
  }

  case "embed":
    return <EmbedElement element={element} attributes={attributes}>{children}</EmbedElement>;

  case "layout":
    return <LayoutElement element={element} attributes={attributes}>{children}</LayoutElement>;

  case "layout-row":
    return <LayoutRowElement element={element} attributes={attributes}>{children}</LayoutRowElement>;

  case "layout-col":
    return <LayoutColElement element={element} attributes={attributes}>{children}</LayoutColElement>;
  case "mention":
    return (
      <span
        {...attributes}
        contentEditable={false}
        data-userid={element.userId}
        title={element.name}
        className={css`
            display: inline-block;
            padding: 2px 8px;
            margin: 0 2px;
            border-radius: 6px;
            background: #e6f0ff;
            border: 1px solid #cfe3ff;
            color: #1d4ed8;
            font-weight: 600;
          `}
      >
          @{element.name}
        {children}
      </span>
    );

  case "tag":
    return (
      <span
        {...attributes}
        contentEditable={false}
        data-tag={element.tag}
        title={`#${element.tag}`}
        className={css`
            display: inline-block;
            padding: 2px 8px;
            margin: 0 2px;
            border-radius: 6px;
            background: #fff4e6;
            border: 1px solid #ffe0b2;
            color: #a55b00;
            font-weight: 600;
          `}
      >
          #{element.tag}
        {children}
      </span>
    );
  case 'page-break':
    return (
      <div
        {...attributes}
        contentEditable={false}
        style={{
          borderTop: '2px dashed #999',
          margin: '2rem 0',
          position: 'relative',
          textAlign: 'center'
        }}
      >
        <span
          style={{
            position: 'absolute',
            top: '-0.8rem',
            left: '50%',
            transform: 'translateX(-50%)',
            background: '#fff',
            padding: '0 0.5rem',
            fontSize: '0.75rem',
            color: '#666'
          }}
        >
            Page Break
        </span>
        {children}
      </div>
    );
  default:
    return <p style={style} {...attributes}>{children}</p>;
  }
};
